/**
 * `/addresses/$id` takes a bare address OR a `chain:address` ref.
 *
 * The route was written for the bare form, with the chain in `?chain=`, and
 * every in-app link builds it that way. A ref reaching it — pasted, or followed
 * from a surface that spells subjects the way `/transactions/$id` does, where
 * `eth:0x…` has always been valid — was handed on whole: the cross-chain read
 * matched no row (so the header said "no catalogued row and no chain in the
 * rollup", which is a sentence about the address, not about the URL) and the
 * feed POST was refused outright with "Invalid field value", because
 * `AddressFeedQuery.addresses[].address` is an address and `eth:0x…` is not one.
 *
 * So the route accepts both spellings, exactly like the transaction route, and
 * this is where the two become one pair. See `route-params-are-refs-not-ids`.
 */
import { toChainSlug } from "@authe/fe-commons/chains";
import { nativeTokenChain } from "@authe/fe-commons/tokens";

/** A parsed `$id`: the address the API wants, and the chain the ref named. */
export interface AddressParam {
  /** Bare address — what every read sends. */
  address: string;
  /** Chain the ref carried, or `null` when it carried none. */
  chain: string | null;
}

/**
 * Split `param` into its address and the chain it names, if any.
 *
 * RIGHT-split, so a CAIP-2 chain keeps its own colon: `eip155:1:0x…` is the
 * chain `eip155:1` and the address `0x…`, not the chain `eip155`. A value with
 * no colon is a bare address and names no chain. Anything that does not leave a
 * non-empty address on the right is returned untouched — a malformed ref is the
 * server's to reject with its own message, not ours to reshape into a different
 * address.
 */
export function parseAddressParam(param: string): AddressParam {
  const raw = (param ?? "").trim();
  const cut = raw.lastIndexOf(":");
  if (cut <= 0) return { address: raw, chain: null };
  const address = raw.slice(cut + 1);
  const chain = raw.slice(0, cut);
  if (!address || !chain) return { address: raw, chain: null };
  return { address, chain };
}

/**
 * The chain whose own coin `param` names — as a slug for the native-asset route
 * — when `param` is a `native:{chain}` token identifier, or `null`.
 *
 * `native:eip155:1` is how the Tokens table spells ETH, so it is what a pasted
 * token identifier or a holdings export carries. Handed to
 * {@link parseAddressParam} it right-splits into the address `1` on a chain
 * called `native:eip155` — a page about nothing, with no error. A chain's own
 * coin has no address and no address page; the route sends it to the view that
 * is about it.
 */
export function nativeAssetParam(param: string): string | null {
  const chain = nativeTokenChain(param);
  return chain ? toChainSlug(chain) ?? chain : null;
}
